import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthContext'
import Sidebar from './Sidebar'
import ThemeToggle from './ThemeToggle'
import logo from '@/assets/vision_controller_logo.jpg'

export default function AppLayout() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  // React Router gives the very first history entry of a session the key
  // "default" (no prior entry to go back to) - a plain, well-established
  // heuristic for "is there anywhere to go back to" without tracking our
  // own navigation stack. Once the user has navigated anywhere (e.g. a
  // sidebar click), this flips true and stays true for the rest of the tab's
  // session, even after going back once.
  //
  // That heuristic alone still shows "Back" on the dashboard itself once
  // it's been reached via a client-side navigation rather than a fresh
  // load (confirmed live: a client role, which has no sidebar to click
  // through, still saw it on their own portfolio list after navigating
  // there and back) - the dashboard is the semantic top of navigation for
  // every role, never a place "Back" is meaningful, so it's excluded here
  // regardless of history state.
  const canGoBack = location.key !== 'default' && location.pathname !== '/'

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-bg-secondary text-text-primary">
      <header className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-navbar px-6">
        <div className="flex items-center gap-3">
          {/* Plain <a>, not react-router's <Link>: a genuine full browser
              reload back to the dashboard, matching the same "click the name
              to reload" behavior as the Details page's site name - not a
              client-side route transition. */}
          <a href="/" className="flex items-center gap-2 hover:opacity-90">
            <img src={logo} alt="Vision Portal" className="h-8 w-8 rounded object-contain" />
            <span className="font-semibold text-brand">Vision Portal</span>
          </a>
          {canGoBack && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              title="Go back"
              aria-label="Go back"
              className="flex items-center gap-1 rounded px-2 py-1 text-sm text-text-secondary hover:bg-bg-secondary hover:text-brand"
            >
              <span aria-hidden="true">←</span> Back
            </button>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm">
          <ThemeToggle />
          <Link to="/profile" className="text-text-secondary hover:text-brand">
            {user?.email}
          </Link>
          <button
            type="button"
            onClick={() => logout()}
            className="rounded px-3 py-1.5 text-text-primary hover:bg-bg-secondary hover:text-brand"
          >
            Logout
          </button>
        </div>
      </header>
      <div className="flex min-h-0 flex-1">
        <Sidebar />
        <main className="min-w-0 flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
